import type { ProseMirrorNode, RichTextField, RichTextTranslation } from "types/rich-text-field";

// The backend serializes RichTextField with PascalCase keys, while the app model is camelCase.
// These raw shapes describe the wire format so we can normalize it before validating/rendering.
interface RawTranslation {
    Locale?: string;
    Content?: unknown;
    Status?: RichTextTranslation["status"];
    TranslatedFromVersion?: string;
    UpdatedAt?: string;
}

interface RawRichTextField {
    Version?: number;
    SourceLocale?: string;
    SourceContent?: unknown;
    SourceContentHash?: string;
    SourceUpdatedAt?: string;
    Translations?: RawTranslation[];
}

function isProseMirrorNode(value: unknown): value is ProseMirrorNode {
    return (
        typeof value === "object" &&
        value !== null &&
        typeof (value as ProseMirrorNode).type === "string"
    );
}

function normalizeTranslation(raw: RawTranslation): RichTextTranslation | undefined {
    if (!isProseMirrorNode(raw.Content)) return undefined;
    return {
        locale: raw.Locale ?? "",
        content: raw.Content,
        status: raw.Status ?? "upToDate",
        translatedFromVersion: raw.TranslatedFromVersion ?? "",
        updatedAt: raw.UpdatedAt ?? "",
    };
}

/**
 * Normalizes the PascalCase RichTextField payload the backend sends into the camelCase
 * {@link RichTextField} the app renders with. Returns `undefined` when the required
 * `SourceContent` ProseMirror doc is missing, so callers can skip rendering malformed data.
 */
function normalizeRichTextField(raw: RawRichTextField): RichTextField | undefined {
    if (raw.Version !== 2 || !isProseMirrorNode(raw.SourceContent)) return undefined;
    return {
        version: 2,
        sourceLocale: raw.SourceLocale ?? "",
        sourceContent: raw.SourceContent,
        sourceContentHash: raw.SourceContentHash ?? "",
        sourceUpdatedAt: raw.SourceUpdatedAt ?? "",
        translations: (raw.Translations ?? [])
            .map(normalizeTranslation)
            .filter((translation) => translation !== undefined),
    };
}

/**
 * Deserializes a JSON-serialized {@link RichTextField} string (e.g. a rule's
 * `experienceBookingConditions`) so it can flow through the same `resolveRichTextField` +
 * `RichTextRenderer` pipeline as authored rich-text templates.
 *
 * The backend serializes with PascalCase keys, so the parsed payload is normalized to the
 * camelCase app model. Returns `undefined` when the string is missing, empty, not valid JSON,
 * or does not match the RichTextField shape, so callers can skip rendering rather than crash.
 * @param json - The JSON string to parse.
 * @returns The parsed RichTextField, or `undefined` when it cannot be parsed/validated.
 */
function jsonToRichTextField(json: string | undefined): RichTextField | undefined {
    if (!json?.trim()) return undefined;

    try {
        const parsed: unknown = JSON.parse(json);
        if (typeof parsed !== "object" || parsed === null) return undefined;
        return normalizeRichTextField(parsed);
    } catch {
        return undefined;
    }
}

export { jsonToRichTextField };
